import { Drawer, IconButton, Typography, Box, Button, CircularProgress, Tooltip } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import LinkIcon from '@mui/icons-material/Link'
import { QueryStatusEnum } from './types'
import { SPACING } from '@common/theme/spacing'
import { SIZING } from '@common/theme/sizing'
import QueryDetailsPanel from './QueryDetailsPanel'
import QueryMessagesPanel from './QueryMessagesPanel'
import QueryActionMenu from './QueryActionMenu'
import { QueryStatus_Input, StudyQueryDetailDataSchema, StudyQueryUpdateSchema } from '@common/config/api/client'
import { useNavigate, useParams } from 'react-router-dom'
import { useStudyQueryMetadata } from '../hooks/useStudyQueriesMetadata'
import { useCombinedPermissions } from '@auth/hooks/useCombinedPermissions'
import { QueryPermission } from '@auth/permissionsEnum'
import { generatePathWithParams } from '@common/utils/urlUtils'
import { ROUTES } from 'src/routes'
import { useExtendedAuth } from '@auth/hooks/useExtendedAuth'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'
import Toast from '@common/components/Toast'
import ConfirmationDialog from '@common/components/ConfirmationDialog/ConfirmationDialog'

export interface ViewOrEditQueryDrawerProps {
  open: boolean
  onClose: () => void
  onUpdateStatus: (
    queryId: string,
    status: QueryStatus_Input,
    reason?: string,
    containreIds?: string[],
    requiresTimepointResubmission?: boolean,
  ) => void
  onSendMessage: (queryId: string, message: string) => void
  onUpdateQuery: (queryId: string, updatedData: StudyQueryUpdateSchema) => void
  query: StudyQueryDetailDataSchema
  isLoading?: boolean
  isError?: boolean
  isUpdatingStatus?: boolean
  isSendingMessage?: boolean
  isUpdatingQuery?: boolean
}

export default function ViewOrEditQueryDrawer({
  open,
  onClose,
  onUpdateStatus,
  onSendMessage,
  onUpdateQuery,
  query,
  isLoading = false,
  isError = false,
  isUpdatingStatus = false,
}: Readonly<ViewOrEditQueryDrawerProps>) {
  const { studyId } = useParams<{ studyId: string }>()
  const navigate = useNavigate()
  const { hasPermission, hasAnyPermission } = useCombinedPermissions()
  const auth = useExtendedAuth()
  const [isCloseConfirmOpen, setIsCloseConfirmOpen] = useState(false)

  useEffect(() => {
    if (!open) {
      setIsCloseConfirmOpen(false)
    }
  }, [open])

  const { data: queryMetadata, isLoading: metadataLoading } = useStudyQueryMetadata(
    {
      study_id: studyId || '',
      site_id: query?.site?.id,
      subject_id: query?.subject?.id,
      timepoint_submission_id: query?.timepoint?.id,
    },
    { enabled: !!studyId && open && !!query?.site?.id },
  )

  const isCCUser = useMemo(() => {
    const currentUserId = auth.user?.profile.sub
    const ccUserIds = query?.cc_users?.map((user) => user.user_id) || []
    return ccUserIds.includes(currentUserId || '')
  }, [auth.user?.profile.sub, query?.cc_users])

  const isAssignee = useMemo(() => {
    const currentUserId = auth.user?.profile.sub
    const assigneeIds = query?.assignees?.map((user) => user.user_id) || []
    return assigneeIds.includes(currentUserId || '')
  }, [auth.user?.profile.sub, query?.assignees])

  const isCreator = useMemo(() => {
    const currentUserId = auth.user?.profile.sub
    return query?.created_by?.id === currentUserId
  }, [auth.user?.profile.sub, query?.created_by?.id])

  const isReadOnly = isCCUser && !isAssignee && !isCreator && !hasPermission(QueryPermission.ALL)

  const handleMarkAsResolved = () => {
    onUpdateStatus(query.id, QueryStatusEnum.RESOLVED)
  }

  const handleSaveChanges = (updatedData: StudyQueryUpdateSchema) => {
    onUpdateQuery(query.id, updatedData)
  }

  const handleCancel = () => {
    onClose()
  }

  const handleDrawerClose = (_event: unknown, reason?: string) => {
    if (reason === 'backdropClick') {
      setIsCloseConfirmOpen(true)
      return
    }
    onClose()
  }

  const handleConfirmClose = () => {
    setIsCloseConfirmOpen(false)
    onClose()
  }

  const handleCancelCloseConfirm = () => {
    setIsCloseConfirmOpen(false)
  }

  const navigateToSubjectTimepoint = () => {
    const path = generatePathWithParams(ROUTES.SUBJECT_TIMEPOINT_SUBMISSION.path, {
      studyId: studyId!,
      subjectId: query.subject?.id,
      timepointSubmissionId: query.timepoint?.id,
    })

    navigate(path)
  }

  const handleCopyLink = () => {
    if (studyId && query) {
      const queryDetailsPath = generatePathWithParams(ROUTES.QUERY_DETAILS.path, {
        studyId,
        queryId: query.id,
      })

      const fullUrl = `${window.location.origin}${queryDetailsPath}`

      navigator.clipboard
        .writeText(fullUrl)
        .then(() => {
          toast(<Toast message="Link copied to clipboard!" variant="success" />)
        })
        .catch((error) => {
          console.error('Failed to copy link:', error)
          toast(<Toast message="Failed to copy link" variant="error" />)
        })
    }
  }

  if (isLoading || metadataLoading || !queryMetadata) {
    return (
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        slotProps={{
          paper: { sx: { width: SIZING.drawerWidthLg } },
        }}
      >
        <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <CircularProgress />
        </Box>
      </Drawer>
    )
  }

  if (isError) {
    return (
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        slotProps={{
          paper: { sx: { width: SIZING.drawerWidthLg } },
        }}
      >
        <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center', height: '100%' }}>
          <Typography color="error">Error loading query details</Typography>
          <Button variant="outlined" onClick={onClose}>
            Close
          </Button>
        </Box>
      </Drawer>
    )
  }

  if (!query) {
    return null
  }

  const queryInfo = {
    id: query.id,
    pseudo_id: query.query_pseudo_id || 'Query',
    status: query.status,
  }

  const timepointSubmissionInfo = {
    name: query.timepoint?.name || 'Timepoint',
    status: query.timepoint?.status || 'Unknown',
  }

  const containerSubmissions =
    query?.linked_container_submissions?.map((container) => ({
      id: container.id,
      name: container.name,
    })) || []

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={handleDrawerClose}
        slotProps={{
          paper: {
            sx: {
              width: SIZING.drawerWidthLg,
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
            },
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            paddingX: SPACING.drawerHeaderSpacingX,
            paddingY: SPACING.drawerHeaderSpacingY,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography variant="h6">Query {query?.query_pseudo_id}</Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="Copy link to query">
              <IconButton aria-label="copy link" size="small" onClick={handleCopyLink}>
                <LinkIcon />
              </IconButton>
            </Tooltip>
            {hasAnyPermission([QueryPermission.UPDATE, QueryPermission.DELETE]) && !isReadOnly ? (
              <QueryActionMenu
                query={queryInfo}
                timepoint={timepointSubmissionInfo}
                containerSubmissions={containerSubmissions}
                onUpdateStatus={onUpdateStatus}
                onGoToTimepoint={navigateToSubjectTimepoint}
              />
            ) : (
              <Button variant="contained" color="secondary" onClick={navigateToSubjectTimepoint}>
                Go to timepoint
              </Button>
            )}
            {hasPermission(QueryPermission.CHANGE_STATUS) && !isReadOnly && (
              <Button
                variant="contained"
                color="primary"
                onClick={handleMarkAsResolved}
                disabled={query?.status === QueryStatusEnum.RESOLVED || isUpdatingStatus}
              >
                {isUpdatingStatus ? 'Updating...' : 'Mark as resolved'}
              </Button>
            )}
            <IconButton onClick={onClose} edge="end" aria-label="close">
              <CloseIcon sx={{ color: 'text.primary' }} />
            </IconButton>
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            height: '100%',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              width: SIZING.drawerWidth,
              height: '100%',
              borderRight: '1px solid',
              borderColor: 'divider',
            }}
          >
            <QueryDetailsPanel
              query={query}
              metadata={queryMetadata}
              onCancel={handleCancel}
              onSave={handleSaveChanges}
              isReadOnly={isReadOnly || !hasPermission(QueryPermission.UPDATE)}
            />
          </Box>

          <Box
            sx={{
              width: SIZING.drawerChatWidth,
              height: '100%',
            }}
          >
            <QueryMessagesPanel query={query} onSendMessage={onSendMessage} isReadOnly={isReadOnly} />
          </Box>
        </Box>
      </Drawer>
      <ConfirmationDialog
        open={isCloseConfirmOpen}
        title="Close query form?"
        description="Any information you have entered will be lost."
        confirmLabel="Close"
        cancelLabel="Cancel"
        onConfirm={handleConfirmClose}
        onCancel={handleCancelCloseConfirm}
      />
    </>
  )
}
